// Recepción DGR — Certificados Tributarios (Lotes Códigos de Barra).
//
// Reemplaza el viejo flujo de "consulta DGR en vivo": los certificados llegan por
// push (fiscalia-ws → WSDGR_2023_Certificados) y acá se procesan en 3 pasos:
//   1. Auditar  → valida/rechaza por su PDF (texto + firma).
//   2. Revisar  → ve el detalle (con Recepción), envía errores, cierra la recepción.
//   3. Generar Lote → arma el lote tributario con los validados → entra al pipeline.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Tabs, Tab, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, IconButton, Chip, TextField, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, Tooltip, Alert,
    ToggleButtonGroup, ToggleButton, MenuItem, TablePagination, LinearProgress,
} from '@mui/material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import SendIcon from '@mui/icons-material/Send';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
    barrasColaAuditarJWT, barrasAuditarExpedienteJWT, barrasColaRevisarJWT,
    barrasDetalleExpJWT, barrasVerPdfCertJWT, barrasFinalizarRecepcionJWT,
    barrasEnviarErroresJWT, barrasValidadosParaLoteJWT, barrasGenerarLoteJWT,
} from '../../api/endpoints';

const COLOR = '#6a1b9a';
const BR = 4;
const fmtMonto = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2 });
const fmtFecha = (f) => f ? new Date(f).toLocaleDateString('es-AR') : '';
const fmtFechaHora = (f) => f ? new Date(f).toLocaleString('es-AR') : '';

// --- Diálogo de confirmación estilizado (NUNCA confirm() nativo) ---
function ConfirmDialog({ open, titulo, mensaje, onClose, onConfirm, loading, color = COLOR, confirmLabel = 'Confirmar', extra }) {
    return (
        <Dialog open={open} onClose={loading ? undefined : onClose} PaperProps={{ sx: { borderRadius: `${BR}px` } }}>
            <DialogTitle sx={{ fontWeight: 700, color }}>{titulo}</DialogTitle>
            <DialogContent sx={{ minWidth: 360 }}>
                <Typography>{mensaje}</Typography>
                {extra}
            </DialogContent>
            <DialogActions sx={{ px: 2.5, pb: 2 }}>
                <Button onClick={onClose} disabled={loading} sx={{ textTransform: 'none' }}>Cancelar</Button>
                <Button onClick={onConfirm} disabled={loading} variant="contained"
                    sx={{ textTransform: 'none', bgcolor: color, '&:hover': { bgcolor: color } }}>
                    {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : confirmLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// --- Paginador reutilizable (client-side) ---
function Paginador({ count, page, rpp, setPage, setRpp }) {
    return (
        <TablePagination component="div" count={count} page={page} rowsPerPage={rpp}
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRpp(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100]} labelRowsPerPage="Por página"
            labelDisplayedRows={({ from, to, count: c }) => `${from}–${to} de ${c.toLocaleString('es-AR')}`} />
    );
}

// --- Diálogo de detalle de certificados de un Exp Adm (paginado server-side) ---
function DialogDetalle({ open, onClose, expAdm }) {
    const { enqueueSnackbar } = useSnackbar();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [loading, setLoading] = useState(false);
    const [campo, setCampo] = useState('CertificadoNro');
    const [valor, setValor] = useState('');
    const [filtro, setFiltro] = useState(null); // { campo, valor } aplicado
    const [exportando, setExportando] = useState(false);

    // Resetear al abrir/cambiar el expediente.
    useEffect(() => { if (open) { setPage(0); setFiltro(null); setValor(''); } }, [open, expAdm]);

    useEffect(() => {
        if (!open || !expAdm) return;
        setLoading(true);
        barrasDetalleExpJWT(expAdm, { page: page + 1, pageSize: rowsPerPage, ...(filtro || {}) })
            .then(r => { setRows(r?.ok ? (r.data || []) : []); setTotal(r?.ok ? (r.total || 0) : 0); })
            .catch(() => enqueueSnackbar('Error al cargar el detalle', { variant: 'error' }))
            .finally(() => setLoading(false));
    }, [open, expAdm, page, rowsPerPage, filtro, enqueueSnackbar]);

    const buscar = () => { setFiltro(valor.trim() ? { campo, valor: valor.trim() } : null); setPage(0); };

    const exportar = async () => {
        setExportando(true);
        try {
            const all = [];
            let pg = 1; const ps = 500;
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const r = await barrasDetalleExpJWT(expAdm, { page: pg, pageSize: ps, ...(filtro || {}) });
                if (!r?.ok) break;
                all.push(...(r.data || []));
                if (all.length >= (r.total || 0) || (r.data || []).length === 0) break;
                pg++;
            }
            if (all.length === 0) { enqueueSnackbar('No hay datos para exportar', { variant: 'info' }); return; }
            const data = all.map(r => ({
                'Recepción': fmtFechaHora(r.Recepcion), 'Impuesto': r.Impuesto, 'Fecha': fmtFecha(r.CertificadoFecha),
                'Certificado': r.CertificadoNro, 'Serie': r.CertificadoLetra, 'Cuenta': r.CuentaImpositiva,
                'Demandado': r.Demandado, 'CUIT/DNI': r.CUIT, 'Domicilio': r.Domicilio, 'CP': r.CP,
                'Neto': r.MontoCapital, 'Intereses': r.MontoResarcitorios, 'Total': r.MontoCertificado,
                'Validado': r.Validado ? 'SI' : (r.Rechazado ? 'NO' : ''), 'Rechazo': r.MotivoRechazo,
            }));
            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Detalle');
            XLSX.writeFile(wb, `detalle_${expAdm}.xlsx`);
            enqueueSnackbar(`${all.length.toLocaleString('es-AR')} filas exportadas`, { variant: 'success' });
        } catch { enqueueSnackbar('Error al exportar', { variant: 'error' }); }
        finally { setExportando(false); }
    };

    const verPdf = async (idCert) => {
        try {
            const r = await barrasVerPdfCertJWT(idCert);
            if (!r?.ok || !r.base64) { enqueueSnackbar(r?.mensaje || 'El certificado no tiene PDF', { variant: 'warning' }); return; }
            const bytes = Uint8Array.from(atob(r.base64), c => c.charCodeAt(0));
            const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch { enqueueSnackbar('Error al abrir el PDF', { variant: 'error' }); }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: `${BR}px`, height: '90vh' } }}>
            <DialogTitle sx={{ fontWeight: 700, color: COLOR }}>
                Detalle del envío — Exp. Adm: <span style={{ fontFamily: 'monospace' }}>{expAdm}</span>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 1.5, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                    <TextField select size="small" value={campo} onChange={e => setCampo(e.target.value)} sx={{ minWidth: 140 }}>
                        <MenuItem value="CertificadoNro">Certificado</MenuItem>
                        <MenuItem value="CertificadoLetra">Serie</MenuItem>
                        <MenuItem value="CuentaImpositiva">Cuenta</MenuItem>
                        <MenuItem value="CUIT">CUIT/DNI</MenuItem>
                    </TextField>
                    <TextField size="small" placeholder="Buscar…" value={valor} onChange={e => setValor(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') buscar(); }}
                        InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ color: '#999', mr: 0.5 }} /> }}
                        sx={{ minWidth: 200 }} />
                    <Button size="small" variant="outlined" onClick={buscar} sx={{ textTransform: 'none', color: COLOR, borderColor: COLOR }}>Buscar</Button>
                    <Box sx={{ flex: 1 }} />
                    <Button size="small" startIcon={exportando ? <CircularProgress size={14} sx={{ color: '#2e7d32' }} /> : <FileDownloadIcon />}
                        onClick={exportar} disabled={exportando} sx={{ textTransform: 'none', color: '#2e7d32' }}>Excel</Button>
                </Box>
                {loading && <Box sx={{ textAlign: 'center', py: 1 }}><CircularProgress size={22} sx={{ color: COLOR }} /></Box>}
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: `${BR}px`, flex: 1, overflow: 'auto' }}>
                    <Table size="small" stickyHeader sx={{ '& td, & th': { fontSize: '0.75rem', py: 0.5, whiteSpace: 'nowrap' } }}>
                            <TableHead>
                                <TableRow sx={{ '& th': { bgcolor: '#ede7f6', color: COLOR, fontWeight: 700, whiteSpace: 'nowrap' } }}>
                                    <TableCell>Recepción</TableCell><TableCell>Imp.</TableCell><TableCell>Fecha</TableCell>
                                    <TableCell>Certificado</TableCell><TableCell>Serie</TableCell><TableCell>Cuenta</TableCell>
                                    <TableCell>Demandado</TableCell><TableCell>CUIT/DNI</TableCell><TableCell>Domicilio</TableCell>
                                    <TableCell>CP</TableCell><TableCell align="right">Total</TableCell>
                                    <TableCell>Validado</TableCell><TableCell>Rechazo</TableCell><TableCell>PDF</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map(r => (
                                    <TableRow key={r.Id} hover sx={{ bgcolor: r.Rechazado ? 'rgba(211,47,47,0.06)' : 'inherit' }}>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtFechaHora(r.Recepcion)}</TableCell>
                                        <TableCell>{r.Impuesto}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtFecha(r.CertificadoFecha)}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>{r.CertificadoNro}</TableCell>
                                        <TableCell>{r.CertificadoLetra}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>{r.CuentaImpositiva}</TableCell>
                                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            <Tooltip title={r.Demandado || ''}><span>{r.Demandado}</span></Tooltip>
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>{r.CUIT}</TableCell>
                                        <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            <Tooltip title={r.Domicilio || ''}><span>{r.Domicilio}</span></Tooltip>
                                        </TableCell>
                                        <TableCell>{r.CP}</TableCell>
                                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{fmtMonto(r.MontoCertificado)}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={r.Validado ? 'SÍ' : (r.Rechazado ? 'NO' : '—')}
                                                color={r.Validado ? 'success' : (r.Rechazado ? 'error' : 'default')} variant="outlined" />
                                        </TableCell>
                                        <TableCell sx={{ color: '#b71c1c', fontSize: '0.7rem' }}>{r.MotivoRechazo}</TableCell>
                                        <TableCell>
                                            <IconButton size="small" onClick={() => verPdf(r.Id)} sx={{ color: COLOR }}>
                                                <PictureAsPdfIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {rows.length === 0 && !loading && (
                                    <TableRow><TableCell colSpan={14} align="center" sx={{ py: 3, color: '#888' }}>Sin certificados.</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                </TableContainer>
                <TablePagination component="div" count={total} page={page} rowsPerPage={rowsPerPage}
                    onPageChange={(e, p) => setPage(p)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[25, 50, 100, 200]} labelRowsPerPage="Por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count.toLocaleString('es-AR')}`} />
            </DialogContent>
            <DialogActions><Button onClick={onClose} sx={{ textTransform: 'none' }}>Cerrar</Button></DialogActions>
        </Dialog>
    );
}

// ============ TAB 1: AUDITAR ============
function TabAuditar({ onAuditado }) {
    const { enqueueSnackbar } = useSnackbar();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [confirm, setConfirm] = useState(null); // { expAdm, total }
    const [procesando, setProcesando] = useState(false);
    const [progreso, setProgreso] = useState(null); // { procesados, total, validados, rechazados }
    const [page, setPage] = useState(0);
    const [rpp, setRpp] = useState(25);

    const [busqueda, setBusqueda] = useState('');
    const cargar = useCallback((q = '') => {
        setLoading(true);
        barrasColaAuditarJWT(q.trim() ? { campo: 'Expediente', valor: q.trim() } : {})
            .then(r => { setRows(r?.ok ? (r.data || []) : []); setPage(0); })
            .catch(() => enqueueSnackbar('Error al cargar la cola', { variant: 'error' }))
            .finally(() => setLoading(false));
    }, [enqueueSnackbar]);
    useEffect(() => { cargar(); }, [cargar]);

    // Audita por tandas (el backend procesa N por llamada) hasta terminar, con progreso.
    const auditar = async () => {
        setProcesando(true);
        const total = confirm.total || 0;
        let procesados = 0, validados = 0, rechazados = 0, quedan = 1;
        setProgreso({ procesados: 0, total, validados: 0, rechazados: 0 });
        try {
            while (quedan > 0) {
                const r = await barrasAuditarExpedienteJWT(confirm.expAdm, 300);
                if (!r?.ok) { enqueueSnackbar(r?.mensaje || 'Error al auditar', { variant: 'error' }); break; }
                procesados += r.auditados; validados += r.validados; rechazados += r.rechazados; quedan = r.quedan;
                setProgreso({ procesados, total: Math.max(total, procesados + quedan), validados, rechazados });
                if (r.auditados === 0) break; // sin avance → cortar para no loopear
            }
            if (quedan === 0) {
                enqueueSnackbar(`Auditoría terminada: ${procesados} procesados (${validados} validados, ${rechazados} rechazados).`,
                    { variant: rechazados > 0 ? 'warning' : 'success' });
            }
            setConfirm(null); setProgreso(null); cargar(); onAuditado?.();
        } catch { enqueueSnackbar('Error al auditar', { variant: 'error' }); }
        finally { setProcesando(false); }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                <TextField size="small" placeholder="Buscar Expediente Adm…" value={busqueda}
                    onChange={e => setBusqueda(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') cargar(busqueda); if (e.key === 'Escape') { setBusqueda(''); cargar(''); } }}
                    InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ color: '#999', mr: 0.5 }} /> }}
                    sx={{ minWidth: 260 }} />
                <Button size="small" variant="outlined" onClick={() => cargar(busqueda)}
                    sx={{ textTransform: 'none', color: COLOR, borderColor: COLOR }}>Buscar</Button>
                <Box sx={{ flex: 1 }} />
                <IconButton onClick={() => cargar(busqueda)} sx={{ color: COLOR }}><RefreshIcon /></IconButton>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: `${BR}px` }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { bgcolor: '#ede7f6', color: COLOR, fontWeight: 700 } }}>
                            <TableCell>Fecha</TableCell><TableCell>Hora</TableCell><TableCell>Expediente Adm</TableCell>
                            <TableCell align="center">Pendientes</TableCell><TableCell align="center">Acción</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}><CircularProgress size={24} sx={{ color: COLOR }} /></TableCell></TableRow>
                        ) : rows.slice(page * rpp, page * rpp + rpp).map(r => (
                            <TableRow key={r.ExpedienteAdm} hover>
                                <TableCell>{fmtFecha(r.Fecha)}</TableCell>
                                <TableCell>{r.Hora ? fmtFechaHora(r.Hora).split(' ')[1] || '' : ''}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.ExpedienteAdm}</TableCell>
                                <TableCell align="center"><Chip size="small" label={r.Pendientes} color="warning" /></TableCell>
                                <TableCell align="center">
                                    <Button size="small" variant="outlined" startIcon={<FactCheckIcon />}
                                        onClick={() => setConfirm({ expAdm: r.ExpedienteAdm, total: r.Pendientes })}
                                        sx={{ textTransform: 'none', color: COLOR, borderColor: COLOR }}>
                                        Auditar
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                        {!loading && rows.length === 0 && (
                            <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: '#888' }}>No hay certificados pendientes de auditar.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {rows.length > rpp && <Paginador count={rows.length} page={page} rpp={rpp} setPage={setPage} setRpp={setRpp} />}
            <ConfirmDialog open={!!confirm} loading={procesando}
                titulo="Auditar certificados"
                mensaje={progreso
                    ? `Auditando ${confirm?.expAdm}…`
                    : `Se va a validar el PDF de cada certificado del expediente ${confirm?.expAdm} (texto + firma). Los válidos quedan validados; los demás, rechazados.${(confirm?.total || 0) > 1000 ? ` Son ${confirm.total.toLocaleString('es-AR')} certificados: se procesa por tandas y puede tardar.` : ''} ¿Continuar?`}
                extra={progreso && (
                    <Box sx={{ mt: 2 }}>
                        <LinearProgress variant={progreso.total ? 'determinate' : 'indeterminate'}
                            value={progreso.total ? Math.min(100, Math.round(progreso.procesados / progreso.total * 100)) : 0}
                            sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: COLOR } }} />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            {progreso.procesados.toLocaleString('es-AR')} de {progreso.total.toLocaleString('es-AR')} · {progreso.validados} validados · {progreso.rechazados} rechazados
                        </Typography>
                    </Box>
                )}
                confirmLabel="Auditar" onClose={() => setConfirm(null)} onConfirm={auditar} />
        </Box>
    );
}

// ============ TAB 2: REVISAR ============
function TabRevisar() {
    const { enqueueSnackbar } = useSnackbar();
    const [estado, setEstado] = useState(0);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [detalle, setDetalle] = useState(null);
    const [confirm, setConfirm] = useState(null); // { tipo, expAdm }
    const [procesando, setProcesando] = useState(false);
    const [page, setPage] = useState(0);
    const [rpp, setRpp] = useState(25);

    const [busqueda, setBusqueda] = useState('');
    const cargar = useCallback((q = '') => {
        setLoading(true);
        barrasColaRevisarJWT(estado, q.trim() ? { campo: 'Expediente', valor: q.trim() } : {})
            .then(r => { setRows(r?.ok ? (r.data || []) : []); setPage(0); })
            .catch(() => enqueueSnackbar('Error al cargar', { variant: 'error' }))
            .finally(() => setLoading(false));
    }, [estado, enqueueSnackbar]);
    useEffect(() => { cargar(); }, [cargar]);

    const exportar = () => {
        if (rows.length === 0) { enqueueSnackbar('No hay datos para exportar', { variant: 'info' }); return; }
        const data = rows.map(r => ({
            'Fecha': fmtFecha(r.Fecha), 'Expediente Adm': r.ExpedienteAdm, 'Registros': r.Registros,
            'Validados': r.Validados, 'Rechazados': r.Rechazados, 'Estado': estado === 1 ? 'Cerrada' : 'Abierta',
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Recepcion DGR');
        XLSX.writeFile(wb, `recepcion_dgr_${estado === 1 ? 'cerradas' : 'abiertas'}.xlsx`);
    };

    const ejecutar = async () => {
        setProcesando(true);
        try {
            let r;
            if (confirm.tipo === 'finalizar') r = await barrasFinalizarRecepcionJWT(confirm.expAdm);
            else r = await barrasEnviarErroresJWT(confirm.expAdm);
            if (r?.ok) { enqueueSnackbar(r.mensaje, { variant: 'success' }); setConfirm(null); cargar(); }
            else enqueueSnackbar(r?.mensaje || 'Error', { variant: 'error' });
        } catch { enqueueSnackbar('Error', { variant: 'error' }); }
        finally { setProcesando(false); }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 2 }}>
                <ToggleButtonGroup size="small" exclusive value={estado} onChange={(e, v) => v != null && setEstado(v)}>
                    <ToggleButton value={0} sx={{ textTransform: 'none' }}>Recepción abierta</ToggleButton>
                    <ToggleButton value={1} sx={{ textTransform: 'none' }}>Cerradas</ToggleButton>
                </ToggleButtonGroup>
                <TextField size="small" placeholder="Buscar Expediente Adm…" value={busqueda}
                    onChange={e => setBusqueda(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') cargar(busqueda); if (e.key === 'Escape') { setBusqueda(''); cargar(''); } }}
                    InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ color: '#999', mr: 0.5 }} /> }}
                    sx={{ minWidth: 240 }} />
                <Button size="small" variant="outlined" onClick={() => cargar(busqueda)}
                    sx={{ textTransform: 'none', color: COLOR, borderColor: COLOR }}>Buscar</Button>
                <Box sx={{ flex: 1 }} />
                <Button size="small" startIcon={<FileDownloadIcon />} onClick={exportar}
                    sx={{ textTransform: 'none', color: '#2e7d32' }}>Excel</Button>
                <IconButton onClick={() => cargar(busqueda)} sx={{ color: COLOR }}><RefreshIcon /></IconButton>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: `${BR}px` }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { bgcolor: '#ede7f6', color: COLOR, fontWeight: 700 } }}>
                            <TableCell>Fecha</TableCell><TableCell>Expediente Adm</TableCell>
                            <TableCell align="center">Registros</TableCell><TableCell align="center">Validados</TableCell>
                            <TableCell align="center">Rechazados</TableCell><TableCell align="center">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3 }}><CircularProgress size={24} sx={{ color: COLOR }} /></TableCell></TableRow>
                        ) : rows.slice(page * rpp, page * rpp + rpp).map(r => (
                            <TableRow key={r.ExpedienteAdm} hover>
                                <TableCell>{fmtFecha(r.Fecha)}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.ExpedienteAdm}</TableCell>
                                <TableCell align="center">{r.Registros}</TableCell>
                                <TableCell align="center"><Chip size="small" label={r.Validados} color="success" variant="outlined" /></TableCell>
                                <TableCell align="center">
                                    {r.Rechazados > 0 ? <Chip size="small" label={r.Rechazados} color="error" variant="outlined" /> : '0'}
                                </TableCell>
                                <TableCell align="center">
                                    <Tooltip title="Ver detalle">
                                        <IconButton size="small" onClick={() => setDetalle(r.ExpedienteAdm)} sx={{ color: COLOR }}><VisibilityIcon fontSize="small" /></IconButton>
                                    </Tooltip>
                                    {r.Rechazados > 0 && (
                                        <Tooltip title="Registrar/Enviar errores a DGR">
                                            <IconButton size="small" onClick={() => setConfirm({ tipo: 'errores', expAdm: r.ExpedienteAdm })} sx={{ color: '#e65100' }}><SendIcon fontSize="small" /></IconButton>
                                        </Tooltip>
                                    )}
                                    {estado === 0 && (
                                        <Tooltip title="Finalizar recepción (habilita generar lote)">
                                            <IconButton size="small" onClick={() => setConfirm({ tipo: 'finalizar', expAdm: r.ExpedienteAdm })} sx={{ color: COLOR }}><LockIcon fontSize="small" /></IconButton>
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                        {!loading && rows.length === 0 && (
                            <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: '#888' }}>Sin expedientes en este estado.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {rows.length > rpp && <Paginador count={rows.length} page={page} rpp={rpp} setPage={setPage} setRpp={setRpp} />}
            <DialogDetalle open={!!detalle} expAdm={detalle} onClose={() => setDetalle(null)} />
            <ConfirmDialog open={!!confirm} loading={procesando}
                color={confirm?.tipo === 'errores' ? '#e65100' : COLOR}
                titulo={confirm?.tipo === 'finalizar' ? 'Finalizar recepción' : 'Enviar errores a DGR'}
                mensaje={confirm?.tipo === 'finalizar'
                    ? `Se cierra la recepción del expediente ${confirm?.expAdm}. Después vas a poder generar el lote con los certificados validados. ¿Continuar?`
                    : `Se registran los certificados rechazados del expediente ${confirm?.expAdm}. (El WS de informe a DGR todavía no está disponible; quedan registrados para comunicarlos.) ¿Continuar?`}
                confirmLabel={confirm?.tipo === 'finalizar' ? 'Finalizar' : 'Registrar'}
                onClose={() => setConfirm(null)} onConfirm={ejecutar} />
        </Box>
    );
}

// ============ TAB 3: GENERAR LOTE ============
function TabGenerarLote() {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [expAdm, setExpAdm] = useState('');
    const [rows, setRows] = useState([]);
    const [sel, setSel] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [generando, setGenerando] = useState(false);
    const [impuesto, setImpuesto] = useState('');
    const [page, setPage] = useState(0);
    const [rpp, setRpp] = useState(50);

    const buscar = async () => {
        if (!expAdm.trim()) { enqueueSnackbar('Ingresá el Expediente Administrativo', { variant: 'info' }); return; }
        setLoading(true); setSel(new Set()); setPage(0);
        try {
            const r = await barrasValidadosParaLoteJWT(expAdm.trim(), { impuesto: impuesto || undefined });
            const data = r?.ok ? (r.data || []) : [];
            setRows(data);
            setSel(new Set(data.map(d => d.Id)));
            if (data.length === 0) enqueueSnackbar('No hay certificados validados sin agrupar para ese expediente.', { variant: 'info' });
        } catch { enqueueSnackbar('Error al buscar', { variant: 'error' }); }
        finally { setLoading(false); }
    };

    const toggle = (id) => setSel(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const toggleTodas = () => setSel(p => p.size === rows.length ? new Set() : new Set(rows.map(r => r.Id)));

    const generar = async () => {
        if (sel.size === 0) { enqueueSnackbar('Seleccioná al menos un certificado', { variant: 'warning' }); return; }
        setGenerando(true);
        try {
            const r = await barrasGenerarLoteJWT({ expAdm: expAdm.trim(), idsCertificados: Array.from(sel) });
            if (r?.ok) {
                enqueueSnackbar(r.mensaje || `Lote creado con ${r.insertadas} causas`, { variant: 'success' });
                navigate(`/carga-masiva/${r.idLote}`);
            } else enqueueSnackbar(r?.mensaje || 'Error al generar el lote', { variant: 'error' });
        } catch { enqueueSnackbar('Error al generar el lote', { variant: 'error' }); }
        finally { setGenerando(false); }
    };

    const montoSel = rows.filter(r => sel.has(r.Id)).reduce((s, r) => s + Number(r.MontoCertificado || 0), 0);

    return (
        <Box>
            <Alert severity="info" sx={{ mb: 1.5, borderRadius: `${BR}px` }}>
                Generá el lote con los certificados <strong>validados</strong> de un expediente cuya recepción ya esté cerrada.
            </Alert>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
                <TextField size="small" label="Expediente Administrativo" value={expAdm}
                    onChange={e => setExpAdm(e.target.value)} onKeyDown={e => e.key === 'Enter' && buscar()}
                    sx={{ minWidth: 240 }} />
                <TextField size="small" select label="Impuesto" value={impuesto} onChange={e => setImpuesto(e.target.value)} sx={{ minWidth: 140 }}>
                    <MenuItem value="">Todos</MenuItem>
                    {['IB', 'CJ', 'SP', 'ES', 'SE', 'MP'].map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)}
                </TextField>
                <Button variant="contained" onClick={buscar} disabled={loading}
                    sx={{ textTransform: 'none', bgcolor: COLOR, '&:hover': { bgcolor: '#4a148c' } }}>
                    {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Buscar'}
                </Button>
            </Box>

            {rows.length > 0 && (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Chip label={`${sel.size} de ${rows.length} seleccionados`} color="primary" />
                        <Chip label={`Total: ${fmtMonto(montoSel)}`} />
                        <Box sx={{ flex: 1 }} />
                        <Button size="small" startIcon={<PlaylistAddCheckIcon />} onClick={toggleTodas} sx={{ textTransform: 'none', color: COLOR }}>
                            {sel.size === rows.length ? 'Deseleccionar todas' : 'Seleccionar todas'}
                        </Button>
                    </Box>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: `${BR}px`, maxHeight: '52vh' }}>
                        <Table size="small" stickyHeader sx={{ '& td, & th': { fontSize: '0.78rem', py: 0.5 } }}>
                            <TableHead>
                                <TableRow sx={{ '& th': { bgcolor: '#ede7f6', color: COLOR, fontWeight: 700 } }}>
                                    <TableCell padding="checkbox">
                                        <Checkbox size="small" checked={sel.size === rows.length} indeterminate={sel.size > 0 && sel.size < rows.length}
                                            onChange={toggleTodas} sx={{ color: COLOR, '&.Mui-checked': { color: COLOR } }} />
                                    </TableCell>
                                    <TableCell>Serie</TableCell><TableCell>Certificado</TableCell><TableCell>Imp.</TableCell>
                                    <TableCell>Cuenta</TableCell><TableCell>Demandado</TableCell><TableCell>CUIT/DNI</TableCell>
                                    <TableCell>CP</TableCell><TableCell align="right">Monto</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.slice(page * rpp, page * rpp + rpp).map(r => {
                                    const s = sel.has(r.Id);
                                    return (
                                        <TableRow key={r.Id} hover onClick={() => toggle(r.Id)} sx={{ cursor: 'pointer', bgcolor: s ? 'rgba(106,27,154,0.06)' : 'inherit' }}>
                                            <TableCell padding="checkbox"><Checkbox size="small" checked={s} sx={{ color: COLOR, '&.Mui-checked': { color: COLOR } }} /></TableCell>
                                            <TableCell>{r.CertificadoLetra}</TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace' }}>{r.CertificadoNro}</TableCell>
                                            <TableCell>{r.Impuesto}</TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace' }}>{r.CuentaImpositiva}</TableCell>
                                            <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                <Tooltip title={r.Demandado || ''}><span>{r.Demandado}</span></Tooltip>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace' }}>{r.CUIT}</TableCell>
                                            <TableCell>{r.CP}</TableCell>
                                            <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{fmtMonto(r.MontoCertificado)}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {rows.length > rpp && <Paginador count={rows.length} page={page} rpp={rpp} setPage={setPage} setRpp={setRpp} />}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                        <Button variant="contained" disabled={generando || sel.size === 0} onClick={generar}
                            sx={{ textTransform: 'none', bgcolor: COLOR, '&:hover': { bgcolor: '#4a148c' } }}>
                            {generando ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : `Generar lote con ${sel.size} certificado(s)`}
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    );
}

// ============ PÁGINA ============
export default function RecepcionBarras() {
    const [tab, setTab] = useState('auditar');
    const navigate = useNavigate();
    return (
        <Box sx={{ p: 2 }}>
            <Paper variant="outlined" sx={{ borderRadius: `${BR}px`, overflow: 'hidden' }}>
                <Box sx={{ p: 2, bgcolor: '#fff', borderBottom: 1, borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate('/carga-masiva')}
                            sx={{ textTransform: 'none', color: COLOR, mr: 0.5 }}>
                            Carga Masiva
                        </Button>
                        <FactCheckIcon sx={{ color: COLOR }} />
                        <Typography variant="h5" sx={{ fontWeight: 700, color: COLOR }}>Recepción DGR — Certificados Tributarios</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Auditar lo que Rentas envía, revisar y generar el lote tributario (entra al pipeline de Carga Masiva).
                    </Typography>
                </Box>
                <Tabs value={tab} onChange={(e, v) => setTab(v)}
                    sx={{ borderBottom: 1, borderColor: 'divider', '& .Mui-selected': { color: `${COLOR} !important` }, '& .MuiTabs-indicator': { bgcolor: COLOR } }}>
                    <Tab value="auditar" label="1. Auditar" sx={{ textTransform: 'none', fontWeight: 600 }} />
                    <Tab value="revisar" label="2. Revisar" sx={{ textTransform: 'none', fontWeight: 600 }} />
                    <Tab value="generar" label="3. Generar Lote" sx={{ textTransform: 'none', fontWeight: 600 }} />
                </Tabs>
                <Box sx={{ p: 2 }}>
                    {tab === 'auditar' && <TabAuditar onAuditado={() => setTab('revisar')} />}
                    {tab === 'revisar' && <TabRevisar />}
                    {tab === 'generar' && <TabGenerarLote />}
                </Box>
            </Paper>
        </Box>
    );
}
